// @ts-strict-ignore
import React, { useMemo } from 'react'
import { Menu, MenuItem } from '../../../components/ui/menu'
import union from 'lodash.union'
import { Popover } from '../../../components/ui/popover'
import { Column } from '../../../types/table'
import { Checkbox } from '../../../components/ui/checkbox'
import { idKey } from '../../../types/constants'

export const selectRow = (
    canSelect,
    canSelectFromAllPages = true,
    showSelectAllWidget,
    setShowSelectAllWidget,
    selectedValues,
    allCheckedState,
    pagination,
    items,
    selectKey,
    onSelectChange,
    extra = {},
    hoverCheckbox = false,
): Column<any, any> => {
    const selectAllWidget = useMemo(() => {
        return (
            <SelectAllWidget
                canSelect={canSelect}
                canSelectFromAllPages={canSelectFromAllPages}
                showSelectAllWidget={showSelectAllWidget}
                setShowSelectAllWidget={setShowSelectAllWidget}
                selectedValues={selectedValues}
                allCheckedState={allCheckedState}
                pagination={pagination}
                items={items}
                selectKey={selectKey}
                onSelectChange={onSelectChange}
            />
        )
    }, [showSelectAllWidget, allCheckedState, selectedValues, items])

    return {
        key: '__select_row',
        title: selectAllWidget,
        noRowClick: true,
        cell: (item) => (
            <CheckboxPure
                selectedValues={selectedValues}
                selectKey={selectKey}
                item={item}
                canSelect={canSelect}
                onSelectChange={onSelectChange}
                hoverCheckbox={hoverCheckbox}
            />
        ),
        ...extra,
    }
}

class CheckboxPure extends React.PureComponent<any> {
    render() {
        const { selectedValues, selectKey, item, canSelect, onSelectChange, hoverCheckbox } =
            this.props

        // If no canSelect filter is provided or if the row passed the canSelect filter`
        if (!canSelect || canSelect(item)) {
            const checked = selectedValues.all
                ? !isSelected(item[selectKey], selectedValues)
                : isSelected(item[selectKey], selectedValues)

            const checkbox = (
                <Checkbox
                    className="mb-0"
                    checked={checked}
                    onChange={(e) =>
                        handleCheckBoxCheck(selectedValues, selectKey, onSelectChange, item, e)
                    }
                />
            )

            if (hoverCheckbox && !checked) {
                return (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-100">
                        {checkbox}
                    </div>
                )
            }

            return checkbox
        }

        return null
    }
}

export const resolveAllCheckedState = (items, selectedValues, selectKey, canSelect) => {
    let len = 0

    if (selectedValues.all) {
        len = selectedValues.blockList.length
    } else {
        len = selectedValues.safeList.length
    }

    if (len === 0) {
        if (selectedValues.all) {
            return true
        }
        return false
    }

    // Check to make sure that all of _this_ page is selected before returning true
    // MER-2802: an entity id may be a number or a string, so key every lookup on String(...).
    const blockKeys = new Set((selectedValues.blockList ?? []).map(idKey))
    const safeKeys = new Set((selectedValues.safeList ?? []).map(idKey))

    let allOnPage = true
    items
        .filter((i) => (canSelect ? canSelect(i) : true))
        .forEach((item) => {
            if (selectedValues.all) {
                if (!blockKeys.has(idKey(item[selectKey]))) {
                    allOnPage = false
                }
            } else {
                if (!safeKeys.has(idKey(item[selectKey]))) {
                    allOnPage = false
                }
            }
        })

    if (allOnPage) {
        return true
    }

    return 'indeterminate'
}

export const handleCheckBoxCheck = (selectedValues, selectKey, onSelectChange, item, e) => {
    const checked = e.target.checked

    if (checked) {
        if (selectedValues.all) {
            const blockList = selectedValues.blockList.filter(
                (x) => idKey(x) !== idKey(item[selectKey]),
            )
            onSelectChange({
                ...selectedValues,
                blockList: blockList,
                selectedCount: selectedValues.selectedCount + 1,
            })
        } else {
            onSelectChange({
                ...selectedValues,
                safeList: [...selectedValues.safeList, item[selectKey]],
                selectedCount: selectedValues.selectedCount + 1,
            })
        }
    } else {
        if (selectedValues.all) {
            onSelectChange({
                ...selectedValues,
                blockList: [...selectedValues.blockList, item[selectKey]],
                selectedCount: selectedValues.selectedCount - 1,
            })
        } else {
            const safeList = selectedValues.safeList.filter(
                (x) => idKey(x) !== idKey(item[selectKey]),
            )
            onSelectChange({
                ...selectedValues,
                safeList: safeList,
                selectedCount: selectedValues.selectedCount - 1,
            })
        }
    }
}

export const SelectAllWidget = ({
    canSelect,
    canSelectFromAllPages = true,
    showSelectAllWidget,
    setShowSelectAllWidget,
    selectedValues,
    allCheckedState,
    pagination,
    items,
    selectKey,
    onSelectChange,
}) => {
    let count = items?.length
    if (canSelect) {
        count = items?.filter(canSelect).length
    }

    const handleSelectRowsFromCurrentPage = (e) => {
        const checked = e.target.checked
        const isIndeterminate = allCheckedState === 'indeterminate'

        if (checked === false && !isIndeterminate) {
            handleDeselectAll()
        } else {
            // check all
            const payload = []
            items
                .filter((i) => (canSelect ? canSelect(i) : true))
                .forEach((i) => payload.push(i[selectKey]))
            onSelectChange({
                all: false,
                safeList: [...selectedValues.safeList, ...payload],
                blockList: [],
                selectedCount: union(selectedValues.safeList, payload).length,
            })
        }
    }

    const handleSelectAllRows = (_) => {
        onSelectChange({
            all: true,
            safeList: [],
            blockList: [],
            selectedCount: pagination?.total,
        })
    }

    const handleDeselectAll = () => {
        onSelectChange({
            all: false,
            safeList: [],
            blockList: [],
            selectedCount: 0,
        })
    }

    const isChecked = selectedValues.all || !!allCheckedState
    const isIndeterminate = allCheckedState === 'indeterminate'
    const acrossPageCount = pagination?.total ?? 0
    const extraCount = acrossPageCount - count
    const showSelectCurrentPage = allCheckedState !== true
    const showSelectAllRows =
        !canSelect && canSelectFromAllPages && !selectedValues.all && acrossPageCount > count
    const showDeselectAll = selectedValues.selectedCount > 0

    const selectCurrentPageAction = {
        key: 'current-page',
        onClick: () => {
            handleSelectRowsFromCurrentPage({ target: { checked: true } })
            setShowSelectAllWidget(false)
        },
        content: (
            <button
                type="button"
                onClick={() => {
                    handleSelectRowsFromCurrentPage({ target: { checked: true } })
                    setShowSelectAllWidget(false)
                }}
                className="flex items-start gap-2.5 w-full px-3 py-2 rounded-md text-left bg-[#F3F4F6] hover:bg-[#EAECF0] transition-colors"
            >
                <svg
                    className="mt-0.5 flex-shrink-0 text-[#2d72d2]"
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                >
                    <path
                        d="M2 4h12M2 8h8M2 12h5"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                    />
                    <path
                        d="M11 10l1.5 1.5L15 9"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium text-[#111827]">
                            All on this page
                        </span>
                        <span className="text-[11px] text-[#6B7280] bg-[#DBE2E8] px-1.5 py-0.5 rounded">
                            {count}
                        </span>
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-0.5">Currently visible rows</div>
                </div>
            </button>
        ),
    }

    const selectAllRowsAction = {
        key: 'all-rows',
        onClick: () => {
            handleSelectAllRows(null)
            setShowSelectAllWidget(false)
        },
        content: (
            <button
                type="button"
                onClick={() => {
                    handleSelectAllRows(null)
                    setShowSelectAllWidget(false)
                }}
                className="flex items-start gap-2.5 w-full px-3 py-2 rounded-md text-left hover:bg-[#F3F4F6] transition-colors"
            >
                <svg
                    className="mt-0.5 flex-shrink-0 text-[#6B7280]"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M7 12l5 5L22 7" />
                    <path d="M2 12l5 5m5-5l5-5" />
                </svg>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium text-[#111827]">
                            All across pages
                        </span>
                        <span className="text-[11px] text-[#6B7280] bg-[#DBE2E8] px-1.5 py-0.5 rounded">
                            {acrossPageCount}
                        </span>
                    </div>
                    <div className="text-[11px] text-[#6B7280] mt-0.5">
                        Includes {extraCount} more not on this page
                    </div>
                </div>
            </button>
        ),
    }

    const deselectAllAction = {
        key: 'deselect-all',
        onClick: () => {
            handleDeselectAll()
            setShowSelectAllWidget(false)
        },
        content: (
            <button
                type="button"
                onClick={() => {
                    handleDeselectAll()
                    setShowSelectAllWidget(false)
                }}
                className="flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-left hover:bg-[#F3F4F6] transition-colors"
            >
                <svg
                    className="flex-shrink-0 text-[#6B7280]"
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                >
                    <rect
                        x="2"
                        y="2"
                        width="12"
                        height="12"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="1.8"
                    />
                </svg>
                <span className="text-[13px] text-[#111827]">Deselect all</span>
            </button>
        ),
    }

    const dropdownActions = [
        ...(showSelectCurrentPage ? [selectCurrentPageAction] : []),
        ...(showSelectAllRows ? [selectAllRowsAction] : []),
        ...(showDeselectAll ? [deselectAllAction] : []),
    ]
    const shouldShowDropdown = dropdownActions.length > 1

    const handleHeaderClick = () => {
        if (dropdownActions.length === 1) {
            dropdownActions[0].onClick()
            return
        }

        setShowSelectAllWidget(!showSelectAllWidget)
    }

    const dropdownContent = (
        <div className="py-1 min-w-[260px]">
            <div className="px-3 py-2 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-widest">
                Selection
            </div>

            {dropdownActions.map((action, index) => (
                <React.Fragment key={action.key}>
                    {action.key === 'deselect-all' && index > 0 && (
                        <div className="my-1 border-t border-[#F3F4F6]" />
                    )}
                    {action.content}
                </React.Fragment>
            ))}
        </div>
    )

    return (
        <Popover
            isOpen={showSelectAllWidget}
            position="bottom-left"
            onClose={() => setShowSelectAllWidget(false)}
            content={dropdownContent}
            popoverClassName="!rounded-lg !shadow-[0_8px_24px_rgba(0,0,0,0.08)] !border !border-[#DBE2E8] !p-1"
            className="block w-full"
            disabled={!shouldShowDropdown}
        >
            <div
                className="flex w-full items-center justify-between cursor-pointer select-none pr-3"
                onClick={handleHeaderClick}
            >
                <Checkbox
                    className="mb-0 pointer-events-none"
                    checked={isChecked}
                    indeterminate={isIndeterminate}
                    onChange={() => {}}
                />
                <span
                    style={{
                        visibility: selectedValues.selectedCount > 0 ? 'visible' : 'hidden',
                        display: 'inline-block',
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: '12px',
                        color: '#374151',
                    }}
                >
                    {selectedValues.selectedCount}
                </span>
            </div>
        </Popover>
    )
}

export const fireOnSelectChange = (selectedValues, onSelectChange) => {
    if (!onSelectChange) {
        return
    }

    const payload = selectedValues

    // remove duplicate ids if there are any
    // this can happen in rare cases
    if (selectedValues.all) {
        payload.blockList = removeDuplicates(selectedValues.blockList)
    } else {
        payload.safeList = removeDuplicates(selectedValues.safeList)
    }

    onSelectChange(payload)
}

const removeDuplicates = (arr) => {
    const s = new Set(arr)
    const it = s.values()
    return Array.from(it)
}

export const isSelected = (key, selectedValues) => {
    // MER-2802: normalise both sides — the id may be a number or a string.
    const wanted = idKey(key)
    if (selectedValues.all) {
        return selectedValues.blockList.some((x) => idKey(x) === wanted)
    } else {
        return selectedValues.safeList.some((x) => idKey(x) === wanted)
    }
}
